import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import useSettingsStore from '@/shared/hooks/useSettingsStore';
import EmojiHappy from '@/shared/icons/flip-ui-kit/large/EmojiHappy';
import InformationIcon from '@/shared/icons/flip-ui-kit/small/Information';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { formatWithNumeral } from '@/shared/utils/numbers';

const BorrowStep1 = ({ onNext }: { onNext: () => void }) => (
  <motion.div className="flex flex-col gap-y-6" {...defaultAnimationProps}>
    <div className="flex flex-col gap-y-3 font-aeonikMedium">
      <div className="text-20 text-white">Get your collateral ready</div>
      <div className="text-14 text-cf-light-2">
        Move assets from your <span className="text-cf-white">Account Balance</span> to your
        <span className="text-cf-white"> Supply Balance</span>. This secures your loan and
        determines how much you can borrow.
      </div>
    </div>
    <div>
      <img src="/borrow-tour-1.png" alt="Borrow Tour 1" className="w-full" />
    </div>
    <div className="flex items-center justify-between">
      <div className="text-14 text-cf-light-2">1 of 3</div>
      <Button onClick={onNext}>Next</Button>
    </div>
  </motion.div>
);

const BorrowStep2 = ({ onNext }: { onNext: () => void }) => (
  <motion.div className="flex flex-col gap-y-6" {...defaultAnimationProps}>
    <div className="flex flex-col gap-y-3 font-aeonikMedium">
      <div className="text-20 text-white">Set your borrowing level</div>
      <div className="text-14 text-cf-light-2">
        Enter the amount you want to borrow and adjust your{' '}
        <span className="text-cf-white">Loan-to-Value (LTV)</span>. Higher LTV gives you more
        liquidity but also higher liquidation risk.
      </div>
      <div className="inline-flex w-[438px] items-center gap-x-1 rounded-md border-cf-gray-4 bg-cf-gray-3-5 px-2 py-3 font-aeonikMedium text-14 text-white">
        <InformationIcon />
        An optimal position usually sits around 50-70% LTV
      </div>
    </div>
    <div>
      <img src="/borrow-tour-2.png" alt="Borrow Tour 2" className="w-full" />
    </div>
    <div className="flex items-center justify-between">
      <div className="text-14 text-cf-light-2">2 of 3</div>
      <Button onClick={onNext}>Next</Button>
    </div>
  </motion.div>
);

const BorrowStep3 = ({ onNext }: { onNext: () => void }) => {
  const { stablecoinsTotalAvailableAmountUsd } = useLendingPools();

  return (
    <motion.div className="flex flex-col gap-y-6" {...defaultAnimationProps}>
      <div className="flex flex-col gap-y-3 font-aeonikMedium">
        <div className="text-20 text-white">Get liquidity without selling your assets</div>
        <div className="text-14 text-cf-light-2">
          Borrow up to{' '}
          <span className="text-cf-white">
            ${formatWithNumeral(stablecoinsTotalAvailableAmountUsd, false)} in stables
          </span>{' '}
          backed by your lending supply. Use it to trade, earn yield, or anything else while keeping
          your exposure intact.
        </div>
      </div>
      <div>
        <img src="/borrow-tour-3.png" alt="Borrow Tour 3" className="w-full" />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-14 text-cf-light-2">3 of 3</div>
        <Button onClick={onNext}>Finish</Button>
      </div>
    </motion.div>
  );
};

const BorrowStep4 = ({ onNext }: { onNext: () => void }) => (
  <motion.div className="flex flex-col items-center gap-y-6" {...defaultAnimationProps}>
    <div className="flex flex-col items-center gap-y-2 py-[40px] text-center font-aeonikMedium">
      <div>
        <EmojiHappy width={72} height={72} className="text-cf-green-3" />
      </div>
      <div className="text-20 text-white">You’re all set</div>
      <div className="max-w-[280px] text-14 text-cf-light-2">
        You now understand how borrowing works. When you’re ready, open your first loan.
      </div>
    </div>
    <Button className="w-full" onClick={onNext}>
      Ready to Borrow
    </Button>
  </motion.div>
);

export const BorrowTourModal = ({
  open,
  onClose,
  onComplete,
}: {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}) => {
  const { borrowTourStep, setSetting } = useSettingsStore();
  const borrowTourStepRef = useRef(borrowTourStep);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    borrowTourStepRef.current = borrowTourStep ?? 1;
    setSetting('borrowTourStep', borrowTourStepRef.current);
  }, [borrowTourStep, open]);

  const handleNext = () => {
    const step = borrowTourStepRef.current;
    if (Number(step) === 4) {
      onClose();
      return;
    }
    setSetting('borrowTourStep', Number(step ?? 1) + 1);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (modalRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      handleNext();
    };
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [open]);

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => {
        setSetting('borrowTourStep', undefined);
        setSetting('isBorrowTourComplete', true);
        onComplete?.();
      }}
      backdropClassName="bg-cf-gray-2/80"
    >
      <div
        ref={modalRef}
        className={classNames(
          'max-w-[510px] rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6  max-md:mx-1 md:min-w-[487px]',
        )}
      >
        <AnimatePresence mode="wait">
          {borrowTourStep === 1 && <BorrowStep1 key="borrow-tour-step-1" onNext={handleNext} />}
          {borrowTourStep === 2 && <BorrowStep2 key="borrow-tour-step-2" onNext={handleNext} />}
          {borrowTourStep === 3 && <BorrowStep3 key="borrow-tour-step-3" onNext={handleNext} />}
          {borrowTourStep === 4 && <BorrowStep4 key="borrow-tour-step-4" onNext={handleNext} />}
        </AnimatePresence>
      </div>
    </DashboardModal>
  );
};
